import { collapsedRangeByDeletingContents } from '../selection';
import { isBlockLevelContent } from './content';
import { addFillerIfContainerEmpty } from './contentMutations';
import {
  getElementContents,
  mutateElementsIntersectingRange,
  setElementTagName,
  unwrapElement,
} from './element';
import {
  findAncestor,
  findNextNonWhitespaceSibling,
  findPreviousNonWhitespaceSibling,
  getNodesIntersectingRange,
  hasAncestor,
  isElement,
} from './node';
import {
  cloneRangeWithPoint,
  createPointAtStartOfNodeContents,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
  findFirstTextPoint,
  PointAffinity,
} from './point';

const OUT_OF_RANGE_INNER_LI_CLASSNAME = 'editor-out-of-range-inner-li';

export type ListElement = HTMLOListElement | HTMLUListElement;

export enum ListType {
  ORDERED = 'ORDERED',
  UNORDERED = 'UNORDERED',
}

export interface ListOperationDelegate {
  decorateList?: (list: ListElement) => void;
}

export const LIST_TYPES_BY_TAG_NAME: { [key: string]: ListType } = {
  OL: ListType.ORDERED,
  UL: ListType.UNORDERED,
};

export const TAG_NAMES_BY_LIST_TYPE: { [K in ListType]: 'ol' | 'ul' } = {
  [ListType.ORDERED]: 'ol',
  [ListType.UNORDERED]: 'ul',
};

export function isListElement(node: Node | null): node is ListElement {
  return isElement(node) && ['OL', 'UL'].includes(node.tagName);
}

export function isOrderedListElement(node: Node): node is HTMLOListElement {
  return isElement(node) && node.tagName === 'OL';
}

export function getListType(element: ListElement): ListType {
  return LIST_TYPES_BY_TAG_NAME[element.tagName];
}

export function isListItemElement(node: Node | null): node is HTMLLIElement {
  return isElement(node) && node.tagName === 'LI';
}

export function createList(
  document: Document,
  type: ListType,
  delegate?: ListOperationDelegate,
): ListElement {
  const element = document.createElement(TAG_NAMES_BY_LIST_TYPE[type]);

  if (delegate && delegate.decorateList) {
    delegate.decorateList(element);
  }

  return element;
}

export function addBulletedListInRange(
  container: Node,
  range: Range,
  type: ListType,
  delegate?: ListOperationDelegate,
): Range {
  return mutateElementsIntersectingRange(
    container,
    range,
    element => isListItemLikeElement(element, container),
    elements => {
      elements.forEach(element => {
        // Turn into a list item if necessary.
        const item = isListItemElement(element) ? element : setElementTagName(element, 'li');
        if (!isListItemElement(item)) {
          return;
        }

        indentListItem(container, item, type, delegate);
      });
    },
  );
}

function markOutOfRangeInnerListItems(items: HTMLLIElement[], range: Range, container: Node) {
  const document = container.ownerDocument;
  if (!document) {
    return;
  }
  items.forEach(item => {
    const itemRange = document.createRange();
    itemRange.setStart(item, 0);
    itemRange.setEndAfter(item);
    const topInnerListItems = getNodesIntersectingRange<HTMLLIElement>( // '**top**InnerListItems' because getNodesIntersectingRange won't drill down in matching nodes.
      item,
      itemRange,
      isListItemElement,
      false,
    );
    Array.from(topInnerListItems).forEach(li => {
      if (!range.intersectsNode(li)) {
        li.classList.add(OUT_OF_RANGE_INNER_LI_CLASSNAME);
      }
    });
  });
}

function unmarkOutOfRangeElements(item: Element | null) {
  if (item === null) {
    return;
  }
  item.querySelectorAll(`.${OUT_OF_RANGE_INNER_LI_CLASSNAME}`).forEach(el => {
    unmarkOutOfRangeElement(el);
  });
}

function unmarkOutOfRangeElement(el: Element) {
  el.classList.remove(OUT_OF_RANGE_INNER_LI_CLASSNAME);
  if (el.classList.length === 0) {
    el.removeAttribute('class');
  }
}

export function indentListItemsInRange(
  container: Node,
  range: Range,
  delegate?: ListOperationDelegate,
): Range {
  return mutateElementsIntersectingRange(
    container,
    range,
    isListItemElement,
    items => {
      // Remove all items nested in an item that will be indented, otherwise it will be indented twice.
      const topItems = items.filter(
        item => !hasAncestor(item, el => el !== item && (items as Node[]).includes(el), container),
      );
      // Mark all inner list items that are out of range to keep them at same horizontal position when indenting their parent.
      markOutOfRangeInnerListItems(topItems, range, container);
      // Indent.
      topItems.forEach(item => {
        const list = findAncestor(item, isListElement, container);
        if (!list) {
          return;
        }

        const type = getListType(list);
        indentListItem(container, item, type, delegate);
      });
      // Unmark out of range list items if any left.
      unmarkOutOfRangeElements(range.commonAncestorContainer.parentElement);
    },

    false,
  );
}

function indentListItem(
  container: Node,
  item: HTMLLIElement,
  type: ListType,
  delegate?: ListOperationDelegate,
) {
  const { ownerDocument: document, parentNode, previousSibling, nextSibling } = item;
  if (!document) {
    return;
  }

  // Outdent all inner items that are out of range (and therefore that user did not wish to indent).
  function outdentInnerListItemsOutOfRange() {
    const nestedListItemsOutOfRange = item.querySelectorAll(`.${OUT_OF_RANGE_INNER_LI_CLASSNAME}`);
    nestedListItemsOutOfRange.forEach(li => {
      if (!isListItemElement(li)) {
        return;
      }
      outdentListItem(item, li);
      unmarkOutOfRangeElement(li);
    });
  }

  // Append this item to the previous sibling, if it is a list of the same type.
  if (isListElement(previousSibling) && getListType(previousSibling) === type) {
    previousSibling.appendChild(item);

    outdentInnerListItemsOutOfRange();
    // Merge in the contents of the next sibling, if it is a list of the same type.
    maybeMergeList(previousSibling);
    return;
  }

  // Append this item to the next sibling, if it is a list of the same type.
  if (isListElement(nextSibling) && getListType(nextSibling) === type) {
    nextSibling.insertBefore(item, nextSibling.firstChild);
    outdentInnerListItemsOutOfRange();
    return;
  }

  if (isListItemElement(previousSibling)) {
    const { lastChild } = previousSibling;

    // Append this item to the next sibling, if it is a list of the same type.
    if (isListElement(lastChild) && getListType(lastChild) === type) {
      lastChild.appendChild(item);
      outdentInnerListItemsOutOfRange();
      return;
    }

    const list = createList(document, type, delegate);
    previousSibling.appendChild(list);
    list.appendChild(item);
    outdentInnerListItemsOutOfRange();
    return;
  }

  if (parentNode) {
    const list = createList(document, type, delegate);
    parentNode.insertBefore(list, item);
    list.appendChild(item);
    outdentInnerListItemsOutOfRange();
  }
}

export function outdentListItemsInRange(container: Node, range: Range): Range {
  return mutateElementsIntersectingRange(
    container,
    range,
    isListItemElement,
    items => {
      // Remove all items nested in an item that will be indented, otherwise it will be indented twice.
      const topItems = items.filter(
        item => !hasAncestor(item, el => el !== item && (items as Node[]).includes(el), container),
      );
      // Mark all inner list items that are out of range to keep them at same horizontal position when outdenting their parent.
      markOutOfRangeInnerListItems(topItems, range, container);
      topItems.forEach(item => {
        outdentListItem(container, item);
      });
      // Unmark out of range list items if any left.
      unmarkOutOfRangeElements(range.commonAncestorContainer.parentElement);
    },
    false,
  );
}

function outdentListItem(container: Node, item: HTMLLIElement) {
  const list = item.parentNode;
  if (!isListElement(list) || list === container) {
    return;
  }

  const { parentNode: listParentNode } = list;
  if (!listParentNode) {
    return;
  }

  // Split the list before the item, if necessary.
  // TODO: Renumber numbered bullets?
  if (findPreviousNonWhitespaceSibling(item)) {
    const splitList = list.cloneNode(false);

    let childNode: Node | null = list.firstChild;
    while (childNode && childNode !== item) {
      const nextChildNode = childNode.nextSibling;
      splitList.appendChild(childNode);
      childNode = nextChildNode;
    }

    listParentNode.insertBefore(splitList, list);
  }

  // Indent all inner items that are out of range (and therefore that user did not wish to outdent).
  const nestedListItemsOutOfRange = item.querySelectorAll(`.${OUT_OF_RANGE_INNER_LI_CLASSNAME}`);
  nestedListItemsOutOfRange.forEach(li => {
    if (!isListItemElement(li)) {
      return;
    }
    indentListItem(item, li, getListType(list));
    unmarkOutOfRangeElement(li);
  });

  // Split the list after the item, if necessary, otherwise just unwrap.
  // TODO: Renumber numbered bullets?
  if (findNextNonWhitespaceSibling(item)) {
    listParentNode.insertBefore(item, list);
    // If node is a nested list item, its next siblings become its children.
    if (isListItemElement(item.parentNode)) {
      item.append(list);
    }
  } else {
    unwrapElement(list);
  }

  // If the item is now the child of another item, make it its sibling.
  if (isListItemElement(item.parentNode)) {
    const { parentNode, nextSibling } = item.parentNode;
    if (!parentNode) {
      return;
    }

    parentNode.insertBefore(item, nextSibling);
  }

  // If the item now no longer the child of a list, turn it into a paragraph.
  if (!isListElement(item.parentNode)) {
    setElementTagName(item, 'div');
  }
}

/** Determines if the element is a list item, or a non-phrasing content container without a list item ancestor. */
function isListItemLikeElement(element: Element, container: Node): boolean {
  if (isListItemElement(element)) {
    return true;
  }

  if (isBlockLevelContent(element)) {
    return !findAncestor(element, isListItemElement, container);
  }

  return false;
}

export function canMergeList(list: ListElement) {
  const { previousSibling, nextSibling } = list;
  const canMergeWithNext = isListElement(nextSibling) && getListType(nextSibling) === getListType(list);
  const canMergeWithPrevious =
    isListElement(previousSibling) && getListType(previousSibling) === getListType(list);
  return canMergeWithNext || canMergeWithPrevious;
}

/**
 * Merge list with its next sibling if it's a list too (of the same type).
 * @param list
 */
export function maybeMergeList(list: ListElement) {
  const { parentNode, previousSibling, nextSibling } = list;
  if (!parentNode) {
    return;
  }

  if (isListElement(nextSibling) && getListType(nextSibling) === getListType(list)) {
    list.appendChild(getElementContents(nextSibling));
    parentNode.removeChild(nextSibling);
  }

  if (isListElement(previousSibling) && getListType(previousSibling) === getListType(list)) {
    previousSibling.appendChild(getElementContents(list));
    parentNode.removeChild(list);
  }
}

export function splitListItemAtRange(container: Node, selectionRange: Range): Range {
  // Delete selected content, if necessary.
  const cursorRange = collapsedRangeByDeletingContents(selectionRange);

  // Hoist the cursor up as much as possible, for a cleaner result. (Example: `<b>^foo</b>` to `^<b>foo</b>`.)
  const cursorPoint = createPointWithEndOfRange(cursorRange);
  const hoistedCursorPoint = cursorPoint.hoist(
    {
      // Stay inside list items.
      condition: node => !isListItemElement(node),
      // Never split.
      splitCondition: () => false,
      // Hoist up when we're the first or last child.
      startCondition: node => !node.previousSibling,
      endCondition: node => !node.nextSibling,
      splitAffinity: PointAffinity.START,
    },
    container,
  );
  const hoistedCursorRange = cloneRangeWithPoint(cursorRange, hoistedCursorPoint);

  // Find the list item under the cursor.
  const listItem = findAncestor(hoistedCursorRange.startContainer, isListItemElement, container);
  if (!listItem || !listItem.parentNode || !listItem.ownerDocument) {
    return cursorRange;
  }

  // Extract the list item content after the cursor.
  const tailRange = hoistedCursorRange.cloneRange();
  tailRange.setEnd(listItem, listItem.childNodes.length);
  const tail = tailRange.extractContents();
  addFillerIfContainerEmpty(listItem);

  // Add it to a new list item.
  const newListItem = listItem.cloneNode(false);
  newListItem.appendChild(tail);
  addFillerIfContainerEmpty(newListItem);
  listItem.parentNode.insertBefore(newListItem, listItem.nextSibling);

  // Find a new place for the cursor.
  const newCursorPoint = findFirstTextPoint(newListItem) || createPointAtStartOfNodeContents(newListItem);
  return cloneRangeWithPoint(cursorRange, newCursorPoint);
}

export function isCursorAtStartOfListItem(container: Node, range: Range, listItem: HTMLLIElement) {
  return (
    range.collapsed &&
    createPointWithStartOfRange(range).isAtStartOfNode(
      listItem,
      node => !findPreviousNonWhitespaceSibling(node),
      container,
    )
  );
}
